import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getUser } from '../lib/api';
import { formatTime, formatDate } from '../lib/utils';
import { ArrowLeft, Clock, FolderKanban, CheckSquare, Calendar, User } from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const COLORS = ['#006CB6', '#F4E600', '#000000', '#808080', '#E5E5E5', '#22c55e', '#ef4444'];

const UserDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getUser(id);
        setData(response.data);
      } catch (error) {
        toast.error('Chyba při načítání dat');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="font-oswald text-xl uppercase tracking-wide">Načítání...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <div className="font-oswald text-xl uppercase">Uživatel nenalezen</div>
      </div>
    );
  }

  const { user, total_minutes, projects, tasks, assigned_tasks, recent_entries, daily_breakdown } = data;

  const projectChartData = projects.slice(0, 6).map(p => ({
    name: p.project_name,
    value: p.total_minutes
  }));

  const taskStatusCount = {
    todo: assigned_tasks.filter(t => t.status === 'todo').length,
    in_progress: assigned_tasks.filter(t => t.status === 'in_progress').length,
    done: assigned_tasks.filter(t => t.status === 'done').length,
  };

  return (
    <div className="space-y-6" data-testid="user-detail">
      {/* Header */}
      <div>
        <Link
          to="/users"
          className="inline-flex items-center gap-2 font-oswald uppercase text-sm tracking-wide text-gray-600 hover:text-black mb-4"
        >
          <ArrowLeft size={16} />
          Zpět na uživatele
        </Link>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-[#F4E600] border-2 border-black flex items-center justify-center">
            <User size={32} />
          </div>
          <div>
            <h1 className="font-oswald text-4xl font-bold uppercase tracking-tight">
              {user.name}
            </h1>
            <p className="font-serif text-gray-600">{user.email}</p>
            <span className={`mt-1 inline-block px-2 py-1 text-xs font-oswald uppercase border ${user.is_admin ? 'bg-[#006CB6] text-white border-[#006CB6]' : 'bg-gray-100 text-gray-800 border-gray-300'}`}>
              {user.is_admin ? 'Admin' : 'Zaměstnanec'}
            </span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-[#F4E600] border-2 border-black">
              <Clock size={24} />
            </div>
            <div>
              <div className="font-oswald text-xs uppercase tracking-wide text-gray-500">Celkový čas</div>
              <div className="font-oswald text-2xl font-bold">{formatTime(total_minutes)}</div>
            </div>
          </div>
        </div>
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-[#006CB6] border-2 border-black">
              <FolderKanban size={24} className="text-white" />
            </div>
            <div>
              <div className="font-oswald text-xs uppercase tracking-wide text-gray-500">Projekty</div>
              <div className="font-oswald text-2xl font-bold">{projects.length}</div>
            </div>
          </div>
        </div>
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-black border-2 border-black">
              <CheckSquare size={24} className="text-white" />
            </div>
            <div>
              <div className="font-oswald text-xs uppercase tracking-wide text-gray-500">Přiřazené úkoly</div>
              <div className="font-oswald text-2xl font-bold">{assigned_tasks.length}</div>
            </div>
          </div>
        </div>
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-500 border-2 border-black">
              <CheckSquare size={24} className="text-white" />
            </div>
            <div>
              <div className="font-oswald text-xs uppercase tracking-wide text-gray-500">Dokončené úkoly</div>
              <div className="font-oswald text-2xl font-bold">{taskStatusCount.done}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Time by Project */}
        {projectChartData.length > 0 && (
          <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
            <h2 className="font-oswald text-xl font-bold uppercase tracking-tight mb-6">
              Čas podle projektů
            </h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={projectChartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${formatTime(value)}`}
                    labelLine={false}
                  >
                    {projectChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatTime(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Daily Activity */}
        {daily_breakdown.length > 0 && (
          <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
            <h2 className="font-oswald text-xl font-bold uppercase tracking-tight mb-6">
              Denní aktivita (posledních 30 dní)
            </h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={daily_breakdown.slice(0, 14).reverse()}>
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontFamily: 'Oswald', fontSize: 10 }}
                    tickFormatter={(d) => d.slice(5)}
                  />
                  <YAxis tickFormatter={(v) => `${Math.round(v/60)}h`} />
                  <Tooltip formatter={(value) => formatTime(value)} labelFormatter={(d) => formatDate(d)} />
                  <Bar dataKey="total_minutes" fill="#006CB6" name="Čas" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Projects Table */}
      <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
        <h2 className="font-oswald text-xl font-bold uppercase tracking-tight mb-6">
          Projekty
        </h2>
        {projects.length === 0 ? (
          <p className="text-center font-serif text-gray-500 py-8">Žádná aktivita na projektech</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left font-oswald uppercase text-xs tracking-wide py-3">Projekt</th>
                  <th className="text-left font-oswald uppercase text-xs tracking-wide py-3">Klient</th>
                  <th className="text-right font-oswald uppercase text-xs tracking-wide py-3">Záznamy</th>
                  <th className="text-right font-oswald uppercase text-xs tracking-wide py-3">Celkový čas</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => (
                  <tr key={project.project_id} className="border-b border-gray-200">
                    <td className="py-3 font-serif font-medium">{project.project_name}</td>
                    <td className="py-3 font-serif text-gray-600">{project.client_name}</td>
                    <td className="py-3 text-right font-serif">{project.entries_count}</td>
                    <td className="py-3 text-right font-oswald font-bold">{formatTime(project.total_minutes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Tasks Table */}
      <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
        <h2 className="font-oswald text-xl font-bold uppercase tracking-tight mb-6">
          Přiřazené úkoly
        </h2>
        {assigned_tasks.length === 0 ? (
          <p className="text-center font-serif text-gray-500 py-8">Žádné přiřazené úkoly</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left font-oswald uppercase text-xs tracking-wide py-3">Úkol</th>
                  <th className="text-left font-oswald uppercase text-xs tracking-wide py-3">Projekt</th>
                  <th className="text-left font-oswald uppercase text-xs tracking-wide py-3">Status</th>
                  <th className="text-left font-oswald uppercase text-xs tracking-wide py-3">Priorita</th>
                  <th className="text-right font-oswald uppercase text-xs tracking-wide py-3">Čas</th>
                </tr>
              </thead>
              <tbody>
                {assigned_tasks.map((task) => (
                  <tr key={task.id} className="border-b border-gray-200">
                    <td className="py-3 font-serif font-medium">{task.title}</td>
                    <td className="py-3 font-serif text-gray-600">{task.project_name}</td>
                    <td className="py-3">
                      <span className={`px-2 py-1 text-xs font-oswald uppercase border ${
                        task.status === 'done' ? 'bg-green-100 text-green-800 border-green-300' :
                        task.status === 'in_progress' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                        'bg-gray-100 text-gray-800 border-gray-300'
                      }`}>
                        {task.status === 'done' ? 'Hotovo' : task.status === 'in_progress' ? 'Rozpracováno' : 'K udělání'}
                      </span>
                    </td>
                    <td className="py-3">
                      <span className={`px-2 py-1 text-xs font-oswald uppercase border ${
                        task.priority === 'high' ? 'bg-red-100 text-red-800 border-red-300' :
                        task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                        'bg-green-100 text-green-800 border-green-300'
                      }`}>
                        {task.priority === 'high' ? 'Vysoká' : task.priority === 'medium' ? 'Střední' : 'Nízká'}
                      </span>
                    </td>
                    <td className="py-3 text-right font-oswald">{formatTime(task.total_time_minutes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Worked Tasks (with time tracked) */}
      {tasks.length > 0 && (
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <h2 className="font-oswald text-xl font-bold uppercase tracking-tight mb-6">
            Úkoly s natrackovaným časem
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left font-oswald uppercase text-xs tracking-wide py-3">Úkol</th>
                  <th className="text-left font-oswald uppercase text-xs tracking-wide py-3">Projekt</th>
                  <th className="text-left font-oswald uppercase text-xs tracking-wide py-3">Status</th>
                  <th className="text-right font-oswald uppercase text-xs tracking-wide py-3">Záznamy</th>
                  <th className="text-right font-oswald uppercase text-xs tracking-wide py-3">Čas</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr key={task.task_id} className="border-b border-gray-200">
                    <td className="py-3 font-serif font-medium">{task.task_title}</td>
                    <td className="py-3 font-serif text-gray-600">{task.project_name}</td>
                    <td className="py-3">
                      <span className={`px-2 py-1 text-xs font-oswald uppercase border ${
                        task.task_status === 'done' ? 'bg-green-100 text-green-800 border-green-300' :
                        task.task_status === 'in_progress' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                        'bg-gray-100 text-gray-800 border-gray-300'
                      }`}>
                        {task.task_status === 'done' ? 'Hotovo' : task.task_status === 'in_progress' ? 'Rozpracováno' : 'K udělání'}
                      </span>
                    </td>
                    <td className="py-3 text-right font-serif">{task.entries_count}</td>
                    <td className="py-3 text-right font-oswald font-bold">{formatTime(task.total_minutes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Time Entries */}
      <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
        <h2 className="font-oswald text-xl font-bold uppercase tracking-tight mb-6">
          Poslední záznamy času
        </h2>
        {recent_entries.length === 0 ? (
          <p className="text-center font-serif text-gray-500 py-8">Žádné záznamy</p>
        ) : (
          <div className="space-y-3">
            {recent_entries.map((entry) => (
              <div
                key={entry.id}
                className="flex items-center gap-4 p-4 border-2 border-gray-200"
              >
                <div className="flex items-center gap-2 text-gray-500">
                  <Calendar size={16} />
                  <span className="font-oswald text-sm">{formatDate(entry.date)}</span>
                </div>
                <div className="flex-1">
                  <div className="font-serif font-medium">{entry.project_name}</div>
                  {entry.task_title && (
                    <div className="text-sm text-gray-600 font-serif">{entry.task_title}</div>
                  )}
                  {entry.description && (
                    <div className="text-sm text-gray-500 font-serif">{entry.description}</div>
                  )}
                </div>
                <div className="font-oswald text-lg font-bold">{formatTime(entry.duration_minutes)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Permissions */}
      <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
        <h2 className="font-oswald text-xl font-bold uppercase tracking-tight mb-4">
          Oprávnění
        </h2>
        <div className="flex flex-wrap gap-2">
          {Object.entries(user.permissions || {}).map(([key, value]) => (
            <span
              key={key}
              className={`px-3 py-1 text-sm font-oswald uppercase border-2 ${
                value ? 'bg-green-100 text-green-800 border-green-300' : 'bg-gray-100 text-gray-400 border-gray-300 line-through'
              }`}
            >
              {key === 'clients' ? 'Klienti' :
               key === 'projects' ? 'Projekty' :
               key === 'tasks' ? 'Úkoly' :
               key === 'time_tracking' ? 'Čas' :
               key === 'revenue' ? 'Tržby' :
               key === 'users' ? 'Uživatelé' : key}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserDetail;
